import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPatch } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Briefcase, Users, FileText, Clock, TrendingUp, ArrowRight,
  Plus, CheckCircle2, XCircle, BarChart3, ChevronRight, RefreshCw, Sparkles, Star,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { useUrlTab } from '../../../lib/hooks/useUrlState';

const STAGE_LABELS = {
  applied: 'Applied', screening: 'Screening', assessment: 'Assessment',
  interview_l1: 'Interview L1', interview_l2: 'Interview L2', interview_l3: 'Interview L3',
  shortlisted: 'Shortlisted', offer: 'Offer', accepted: 'Accepted', rejected: 'Rejected', withdrawn: 'Withdrawn', joined: 'Joined',
};

const KPI = ({ icon: Icon, label, value, sub, tone = 'default', testId }) => {
  const tones = {
    default: 'bg-white border-slate-200', amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200', green: 'bg-emerald-50 border-emerald-200',
  };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500">
        <Icon size={13} /> {label}
      </div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#3b4872]">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const STATUS_BADGE = {
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  approved:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-red-50 text-red-600 border-red-200',
  converted: 'bg-violet-50 text-violet-700 border-violet-200',
  draft:     'bg-slate-100 text-slate-600 border-slate-200',
};

const EMPTY_MRF = {
  department: '', role_title: '', headcount: 1, project_name: '',
  justification: '', required_by: '', employment_type: 'full_time', budget_ctc: '',
};

export default function RecruitmentDashboard() {
  const [d, setD] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useUrlTab('tab', 'pipeline');
  const [mrfs, setMrfs] = useState([]);
  const [showMrfForm, setShowMrfForm] = useState(false);
  const [mrfForm, setMrfForm] = useState(EMPTY_MRF);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const [poolCount, setPoolCount] = useState(0);

  const load = useCallback(async () => {
    apiGet('/hr/recruitment/dashboard').then(setD).catch(() => setD({}));
    apiGet('/hr/jobs', { status: 'open' }).then((r) => setJobs(r.items || [])).catch(() => {});
    apiGet('/hr/mrf').then((r) => setMrfs(r.items || [])).catch(() => {});
    apiGet('/hr/recruitment/pool').then(r => setPoolCount(r.count || 0)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitMrf = async (e) => {
    e.preventDefault();
    if (!mrfForm.department || !mrfForm.role_title || !mrfForm.justification || !mrfForm.required_by) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      await apiPost('/hr/mrf', {
        ...mrfForm,
        headcount: parseInt(mrfForm.headcount) || 1,
        budget_ctc: mrfForm.budget_ctc ? parseFloat(mrfForm.budget_ctc) : undefined,
      });
      toast.success('MRF submitted');
      setShowMrfForm(false);
      setMrfForm(EMPTY_MRF);
      apiGet('/hr/mrf').then((r) => setMrfs(r.items || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const approveMrf = async (id) => {
    try {
      await apiPost(`/hr/mrf/${id}/approve`);
      toast.success('MRF approved'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const convertMrf = async (id) => {
    try {
      const r = await apiPost(`/hr/mrf/${id}/convert-to-job`);
      toast.success('Job opening created');
      nav(`/app/hr/recruitment/jobs/${r.job.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!d) return <div className="p-8 text-slate-500" data-testid="recruitment-loading">Loading…</div>;

  const totalOpen = Object.values(d.open_positions_by_dept || {}).reduce((a, b) => a + b, 0);
  const pipeline = d.pipeline_by_stage || {};
  const activeStages = ['applied', 'screening', 'assessment', 'interview_l1', 'interview_l2', 'interview_l3', 'shortlisted', 'offer'];

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="recruitment-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Recruitment</h1>
          <p className="text-sm text-slate-600 mt-1">Hiring pipeline · Offers · Time-to-hire.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav('/app/hr/recruitment/board')} data-testid="view-board-btn"
            className="px-3 py-2 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#3b4872]/5 flex items-center gap-2">
            <Users size={13} /> Job Board
          </button>
          <button onClick={() => nav('/app/hr/recruitment/jobs')} data-testid="view-jobs-btn"
            className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2">
            <Briefcase size={14} /> Job Openings
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Briefcase} label="Open Positions" value={totalOpen} sub={`${Object.keys(d.open_positions_by_dept || {}).length} departments`} testId="kpi-open-positions" />
        <KPI icon={Users} label="In Pipeline" value={activeStages.reduce((a, s) => a + (pipeline[s] || 0), 0)} sub="Active candidates" testId="kpi-in-pipeline" />
        <KPI icon={FileText} label="Offers Pending" value={d.offers_pending_acceptance || 0} sub="Awaiting acceptance" tone={d.offers_pending_acceptance ? 'amber' : 'default'} testId="kpi-offers-pending" />
        <KPI icon={Clock} label="Closing This Week" value={d.positions_closing_this_week || 0} sub="Job deadlines" tone={d.positions_closing_this_week ? 'red' : 'default'} testId="kpi-closing-week" />
      </div>

      {/* Candidate Pool quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => nav('/app/hr/recruitment/pool')} className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-lg hover:border-[#3b4872] transition-colors text-left" data-testid="pool-link">
          <Users size={20} className="text-[#3b4872]" />
          <div>
            <p className="font-display font-bold text-sm">Candidate Pool</p>
            <p className="text-xs text-slate-400">{poolCount} candidate(s) ready</p>
          </div>
        </button>
        <button onClick={() => nav('/app/hr/recruitment/scanner')} className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-lg hover:border-purple-400 transition-colors text-left" data-testid="scanner-link">
          <Sparkles size={20} className="text-purple-500" />
          <div>
            <p className="font-display font-bold text-sm">Resume Scanner</p>
            <p className="text-xs text-slate-400">AI-parse resumes instantly</p>
          </div>
        </button>
        <button onClick={() => nav('/app/hr/recruitment/matches')} className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-lg hover:border-amber-400 transition-colors text-left" data-testid="matches-link">
          <Star size={20} className="text-amber-500" />
          <div>
            <p className="font-display font-bold text-sm">Job Matches</p>
            <p className="text-xs text-slate-400">AI-matched pool → open roles</p>
          </div>
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200">
        {[['pipeline', 'Pipeline'], ['mrf', `MRF (${mrfs.length})`], ['analytics', 'Analytics']].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-xs font-display font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid={`tab-${id}`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Pipeline tab */}
      {tab === 'pipeline' && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card data-testid="pipeline-funnel-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                <TrendingUp size={15} /> Pipeline by Stage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {activeStages.map((s) => {
                const v = pipeline[s] || 0;
                const max = Math.max(1, ...activeStages.map((x) => pipeline[x] || 0));
                return (
                  <div key={s} className="flex items-center gap-3" data-testid={`funnel-${s}`}>
                    <div className="w-28 text-[11px] font-display font-bold text-slate-600">{STAGE_LABELS[s]}</div>
                    <div className="flex-1 bg-slate-100 rounded-sm h-5 overflow-hidden">
                      <div className="h-full bg-[#3b4872] transition-all" style={{ width: `${(v / max) * 100}%` }} />
                    </div>
                    <div className="w-8 text-right text-xs font-display font-bold">{v}</div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card data-testid="open-jobs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                <Briefcase size={15} /> Open Positions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-xs text-slate-400 py-6 text-center">No open positions.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {jobs.map((j) => (
                    <button key={j.id} onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)} data-testid={`dash-job-${j.id}`}
                      className="w-full flex items-center justify-between py-2.5 hover:bg-slate-50 px-2 -mx-2 rounded-sm text-left">
                      <div>
                        <div className="font-display font-bold text-sm">{j.title}</div>
                        <div className="text-[11px] text-slate-500">{j.department || '—'} · {j.headcount} opening(s)</div>
                      </div>
                      <ArrowRight size={15} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* MRF tab */}
      {tab === 'mrf' && (
        <div className="space-y-4" data-testid="mrf-tab">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">Manpower Requisition Forms — department heads raise headcount requests here.</div>
            <button onClick={() => setShowMrfForm(!showMrfForm)} data-testid="new-mrf-btn"
              className="flex items-center gap-1.5 px-3 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c]">
              <Plus size={13} /> New MRF
            </button>
          </div>

          {showMrfForm && (
            <form onSubmit={submitMrf} className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="mrf-form">
              <div className="font-display font-bold text-sm">New Manpower Requisition</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Department *</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={mrfForm.department} onChange={e => setMrfForm({ ...mrfForm, department: e.target.value })} placeholder="e.g. Civil Engineering" data-testid="mrf-department" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Role Title *</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={mrfForm.role_title} onChange={e => setMrfForm({ ...mrfForm, role_title: e.target.value })} placeholder="e.g. Site Engineer" data-testid="mrf-role-title" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Headcount</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" type="number" min="1" value={mrfForm.headcount} onChange={e => setMrfForm({ ...mrfForm, headcount: e.target.value })} data-testid="mrf-headcount" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Required By *</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" type="date" value={mrfForm.required_by} onChange={e => setMrfForm({ ...mrfForm, required_by: e.target.value })} data-testid="mrf-required-by" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Project (optional)</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={mrfForm.project_name} onChange={e => setMrfForm({ ...mrfForm, project_name: e.target.value })} placeholder="Project Sanand" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Employment Type</label>
                  <select className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" value={mrfForm.employment_type} onChange={e => setMrfForm({ ...mrfForm, employment_type: e.target.value })}>
                    {['full_time', 'contract', 'intern'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Budget CTC (₹ LPA, optional)</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" type="number" value={mrfForm.budget_ctc} onChange={e => setMrfForm({ ...mrfForm, budget_ctc: e.target.value })} placeholder="e.g. 8.5" /></div>
              </div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Justification *</label><textarea rows={2} className="inp w-full resize-none" value={mrfForm.justification} onChange={e => setMrfForm({ ...mrfForm, justification: e.target.value })} placeholder="Why is this headcount needed?" data-testid="mrf-justification" /></div>
              <div className="flex gap-2">
                <button type="submit" disabled={saving} className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50" data-testid="mrf-submit-btn">{saving ? 'Submitting…' : 'Submit MRF'}</button>
                <button type="button" onClick={() => setShowMrfForm(false)} className="px-4 py-2 border border-slate-300 text-slate-600 rounded-sm text-xs font-display font-bold uppercase tracking-wider">Cancel</button>
              </div>
            </form>
          )}

          {mrfs.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-10 bg-white border border-slate-200 rounded-sm">No MRFs yet. Department heads can raise requisitions here.</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
              {mrfs.map((m) => (
                <div key={m.id} className="p-4 flex items-start justify-between gap-3" data-testid={`mrf-row-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm">{m.role_title} — {m.department}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {m.headcount} position(s) · by {m.required_by} · {m.employment_type}
                      {m.project_name && ` · ${m.project_name}`}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 line-clamp-1">{m.justification}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-display font-bold uppercase px-2 py-0.5 border rounded-sm ${STATUS_BADGE[m.status] || ''}`} data-testid={`mrf-status-${m.id}`}>{m.status}</span>
                    {m.status === 'submitted' && (
                      <button onClick={() => approveMrf(m.id)} data-testid={`mrf-approve-${m.id}`}
                        className="px-2 py-1 bg-emerald-600 text-white rounded-sm text-[10px] font-bold flex items-center gap-1">
                        <CheckCircle2 size={11} /> Approve
                      </button>
                    )}
                    {m.status === 'approved' && !m.converted_job_id && (
                      <button onClick={() => convertMrf(m.id)} data-testid={`mrf-convert-${m.id}`}
                        className="px-2 py-1 bg-[#3b4872] text-white rounded-sm text-[10px] font-bold flex items-center gap-1">
                        <ChevronRight size={11} /> Create Job
                      </button>
                    )}
                    {m.converted_job_id && (
                      <button onClick={() => nav(`/app/hr/recruitment/jobs/${m.converted_job_id}`)}
                        className="px-2 py-1 border border-violet-300 text-violet-700 rounded-sm text-[10px] font-bold">
                        View Job
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics tab */}
      {tab === 'analytics' && (
        <div className="space-y-5" data-testid="analytics-tab">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <AnalyticsKPI label="Offer → Join Ratio" value={d.offer_to_join_ratio != null ? `${(d.offer_to_join_ratio * 100).toFixed(0)}%` : '—'} sub="Candidates who join after accepting" />
            <AnalyticsKPI label="Interview → Offer Ratio" value={d.interview_to_offer_ratio != null ? `${(d.interview_to_offer_ratio * 100).toFixed(0)}%` : '—'} sub="Interviewees who get an offer" />
            <AnalyticsKPI label="Assessment Pass Rate" value={d.assessment_pass_rate != null ? `${(d.assessment_pass_rate * 100).toFixed(0)}%` : '—'} sub="Candidates who pass the test" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Card data-testid="source-effectiveness-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                  <BarChart3 size={15} /> Source Effectiveness (hires)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(d.source_effectiveness || {}).length === 0 ? (
                  <div className="text-xs text-slate-400 py-6 text-center">No hires yet.</div>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(d.source_effectiveness).sort((a, b) => b[1] - a[1]).map(([src, cnt]) => {
                      const total = Object.values(d.source_effectiveness).reduce((a, b) => a + b, 0);
                      return (
                        <div key={src} className="flex items-center gap-3" data-testid={`source-${src}`}>
                          <div className="w-28 text-[11px] font-display font-bold text-slate-600 truncate capitalize">{src.replace(/_/g, ' ')}</div>
                          <div className="flex-1 bg-slate-100 rounded-sm h-5">
                            <div className="h-full bg-emerald-500 rounded-sm transition-all" style={{ width: `${(cnt / total) * 100}%` }} />
                          </div>
                          <div className="w-8 text-right text-xs font-display font-bold">{cnt}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="days-to-hire-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
                  <Clock size={15} /> Avg Days to Hire
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(d.avg_days_to_hire_by_dept || {}).length === 0 ? (
                  <div className="text-xs text-slate-400 py-6 text-center">No completed hires yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {Object.entries(d.avg_days_to_hire_by_dept).map(([dept, days]) => (
                      <div key={dept} className="flex items-center justify-between text-xs border-b border-slate-100 py-1.5">
                        <span className="text-slate-600">{dept}</span>
                        <span className="font-display font-bold text-[#3b4872]">{days} days</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsKPI({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#3b4872]">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
